import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/text'
import { debounce } from '@solid-primitives/scheduled'
import { useQuery } from '@tanstack/solid-query'
import { SlidersHorizontal, X } from 'lucide-solid'
import { For, Show, createSignal } from 'solid-js'
import { useService } from 'solid-services'
import { css } from 'styled-system/css'
import { Box, Circle, Flex, Grid } from 'styled-system/jsx'
import { CATEGORY_MAP } from './categories'
import { CATEGORY_FILTERS, type FilterDef, countActive, optionLabels } from './filters'
import { useGlobalSearch } from './state'

const option = (active: boolean) =>
  css({
    h: '7',
    px: '2.5',
    borderRadius: 'full',
    borderWidth: '1px',
    borderColor: active ? 'jade.8' : 'gray.7',
    bg: active ? 'jade.a3' : 'transparent',
    color: active ? 'fg.default' : 'fg.muted',
    fontSize: 'xs',
    fontWeight: 'medium',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background 0.15s, border-color 0.15s, color 0.15s',
    _hover: { color: 'fg.default', borderColor: active ? 'jade.8' : 'gray.9' },
  })

const chipRemove = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '1',
  h: '7',
  pl: '2.5',
  pr: '1.5',
  borderRadius: 'full',
  borderWidth: '1px',
  borderColor: 'jade.8',
  bg: 'jade.a3',
  fontSize: 'xs',
  fontWeight: 'medium',
  '& button': { display: 'inline-flex', color: 'fg.subtle', cursor: 'pointer', _hover: { color: 'fg.default' } },
})

/** Type-to-find filter (symbols, exchanges, members): suggestions appear inline, picks become chips */
function SearchField(props: { def: FilterDef }) {
  const s = useService(useGlobalSearch)()
  const [term, setTerm] = createSignal('')
  const [debounced, setDebounced] = createSignal('')
  const commit = debounce((v: string) => setDebounced(v.trim()), 250)

  const selected = () => {
    const v = s.categoryFilters()[props.def.key]
    return Array.isArray(v) ? v : []
  }

  const suggestions = useQuery(() => ({
    queryKey: ['search-filter-find', props.def.key, debounced()],
    queryFn: () => props.def.find!(debounced()),
    enabled: debounced().length > 0,
    staleTime: 1000 * 60,
  }))

  const pick = (value: string) => {
    if (!selected().includes(value)) s.setFilter(props.def.key, [...selected(), value])
    setTerm('')
    setDebounced('')
  }

  return (
    <Grid gap='2'>
      <Text fontSize='xs' fontWeight='semibold' color='fg.muted'>{props.def.label}</Text>
      <Show when={selected().length}>
        <Flex gap='1.5' flexWrap='wrap'>
          <For each={selected()}>
            {(v) => (
              <span class={chipRemove}>
                {optionLabels.get(v) ?? v}
                <button
                  type='button'
                  aria-label={`Remove ${optionLabels.get(v) ?? v}`}
                  onClick={() => s.setFilter(props.def.key, selected().filter((x) => x !== v))}
                >
                  <X size={12} />
                </button>
              </span>
            )}
          </For>
        </Flex>
      </Show>
      <input
        value={term()}
        onInput={(e) => {
          setTerm(e.currentTarget.value)
          commit(e.currentTarget.value)
        }}
        placeholder={props.def.placeholder}
        aria-label={props.def.label}
        class={css({
          h: '8',
          px: '2.5',
          borderRadius: 'l2',
          borderWidth: '1px',
          borderColor: 'gray.7',
          bg: 'bg.default',
          fontSize: { base: 'xs', smDown: '16px' },
          outline: 'none',
          _focus: { borderColor: 'jade.8' },
        })}
      />
      <Show when={debounced()}>
        <Grid gap='0.5'>
          <Show when={suggestions.isFetching && !suggestions.data}>
            <Text fontSize='xs' color='fg.subtle' px='1'>Searching…</Text>
          </Show>
          <Show when={suggestions.data && suggestions.data.length === 0}>
            <Text fontSize='xs' color='fg.subtle' px='1'>No matches</Text>
          </Show>
          <For each={(suggestions.data ?? []).filter((o) => !selected().includes(o.value))}>
            {(o) => (
              <button
                type='button'
                class={css({
                  textAlign: 'left',
                  fontSize: 'xs',
                  px: '2',
                  py: '1.5',
                  borderRadius: 'l1',
                  cursor: 'pointer',
                  _hover: { bg: 'bg.muted' },
                })}
                onClick={() => pick(o.value)}
              >
                {o.label}
              </button>
            )}
          </For>
        </Grid>
      </Show>
    </Grid>
  )
}

function Field(props: { def: FilterDef }) {
  if (props.def.type === 'search') return <SearchField def={props.def} />
  return <OptionsField def={props.def} />
}

function OptionsField(props: { def: FilterDef }) {
  const s = useService(useGlobalSearch)()
  const value = () => s.categoryFilters()[props.def.key]

  // Options that come from the database (courses, groups) are loaded once and cached
  const loaded = useQuery(() => ({
    queryKey: ['search-filter-options', props.def.key, props.def.label],
    queryFn: () => props.def.load!(),
    enabled: !!props.def.load,
    staleTime: 1000 * 60 * 10,
  }))
  const options = () => props.def.options ?? loaded.data ?? []

  const isOn = (v: string) => {
    const current = value()
    if (props.def.type === 'multi') return Array.isArray(current) && current.includes(v)
    return (current ?? '') === v
  }

  const toggle = (v: string) => {
    if (props.def.type === 'single') return s.setFilter(props.def.key, v)
    const current = Array.isArray(value()) ? (value() as string[]) : []
    s.setFilter(props.def.key, current.includes(v) ? current.filter((x) => x !== v) : [...current, v])
  }

  return (
    <Grid gap='2'>
      <Flex justifyContent='space-between' alignItems='baseline'>
        <Text fontSize='xs' fontWeight='semibold' color='fg.muted'>{props.def.label}</Text>
        <Show when={props.def.type === 'multi'}>
          <Text fontSize='11px' color='fg.subtle'>
            {Array.isArray(value()) && value()!.length ? `${value()!.length} selected` : 'Any'}
          </Text>
        </Show>
      </Flex>
      <Flex
        gap='1.5'
        flexWrap='wrap'
        role={props.def.type === 'single' ? 'radiogroup' : 'group'}
        aria-label={props.def.label}
      >
        <Show when={props.def.load && loaded.isPending}>
          <Text fontSize='xs' color='fg.subtle'>Loading…</Text>
        </Show>
        <For each={options()}>
          {(o) => (
            <button
              type='button'
              role={props.def.type === 'single' ? 'radio' : 'checkbox'}
              aria-checked={isOn(o.value)}
              class={option(isOn(o.value))}
              onClick={() => toggle(o.value)}
            >
              {o.label}
            </button>
          )}
        </For>
      </Flex>
    </Grid>
  )
}

/** Filters for the selected category; shown to the right of the results (or in their place on phones) */
export function SearchFilterPanel(props: { onDone?: () => void; resultCount?: number }) {
  const s = useService(useGlobalSearch)()
  const defs = () => (s.category() ? CATEGORY_FILTERS[s.category()!] : [])
  const active = () => countActive(s.categoryFilters())

  return (
    <Flex direction='column' h='full' minH='0'>
      <Flex justifyContent='space-between' alignItems='center' px='4' pt='3' pb='2'>
        <Text fontSize='11px' fontWeight='semibold' letterSpacing='.06em' textTransform='uppercase' color='fg.subtle'>
          {s.category() ? `${CATEGORY_MAP[s.category()!].label} filters` : 'Filters'}
        </Text>
        <Show when={active()}>
          <button
            type='button'
            class={css({ fontSize: 'xs', color: 'jade.11', cursor: 'pointer', _hover: { textDecoration: 'underline' } })}
            onClick={s.clearFilters}
          >
            Clear
          </button>
        </Show>
      </Flex>
      <Grid gap='4' px='4' pb='4' flex='1' minH='0' overflowY='auto' alignContent='start'>
        <For each={defs()}>{(def) => <Field def={def} />}</For>
      </Grid>
      <Show when={props.onDone}>
        <Box px='4' py='3' borderTopWidth='1px' borderColor='border'>
          <Button w='full' colorPalette='jade' onClick={props.onDone}>
            {props.resultCount === undefined
              ? 'Show results'
              : `Show ${props.resultCount >= 100 ? '100+' : props.resultCount} result${props.resultCount === 1 ? '' : 's'}`}
          </Button>
        </Box>
      </Show>
    </Flex>
  )
}

/** Toggle shown on the right of the search input once a category is selected */
export function SearchFilterToggle() {
  const s = useService(useGlobalSearch)()
  const active = () => countActive(s.categoryFilters())

  return (
    <button
      type='button'
      aria-pressed={s.showFilters()}
      aria-label={active() ? `Filters, ${active()} active` : 'Filters'}
      class={css({
        display: 'inline-flex',
        alignItems: 'center',
        gap: '1.5',
        h: '8',
        px: '2.5',
        flexShrink: 0,
        borderRadius: 'l2',
        borderWidth: '1px',
        borderColor: s.showFilters() || active() ? 'jade.8' : 'border',
        bg: s.showFilters() ? 'jade.a3' : 'transparent',
        color: 'fg.default',
        fontSize: 'xs',
        fontWeight: 'medium',
        cursor: 'pointer',
        _hover: { borderColor: 'jade.8' },
      })}
      onClick={() => s.setShowFilters(!s.showFilters())}
    >
      <SlidersHorizontal size={14} />
      <Box as='span' display={{ base: 'inline', smDown: 'none' }}>Filters</Box>
      <Show when={active()}>
        <Circle as='span' minW='4.5' h='4.5' px='1' bg='jade.9' color='white' fontSize='10px' fontWeight='bold'>
          {active()}
        </Circle>
      </Show>
    </button>
  )
}
